import _ from 'lodash';
import React, {useCallback, useEffect, useMemo} from 'react';

import {Analytics} from '../../../../../services/analytics';
import {useMemoizedSelector} from '../../../../../state/hooks';
import {useRunsData, useRunSetsQuery} from '../../../../../state/runs/hooks';
import type * as PanelTypes from '../../../../../state/views/panel/types';
import {type Ref as SectionRef} from '../../../../../state/views/panelBankSectionConfig/types';
import {recordRumActionOncePerView} from '../../../../../util/datadog/datadogRum';
import {panelViews} from '../../../../PanelBank/types';
import {
  type MediaBrowserPanelConfig,
  Spec,
} from '../../../../PanelMediaBrowser';
import {useBoundingBoxes} from '../../../../PanelMediaBrowser/hooks/useBoundingBoxes';
import {useSliderRanges} from '../../../../PanelMediaBrowser/hooks/useSliderRanges';
import {useMediaCardTypes} from '../../../../PanelMediaBrowser/useGetMediaCardTypes';
import {useConfigWithDefaults} from '../../../../PanelMediaBrowser/useMediaPanelValues';
import {getMediaCardTypeFromHistories} from '../../../../PanelMediaBrowser/utils/getMediaCardTypeFromHistories';
import {usePanelsSharedRefsContext} from '../../../../Views/contexts/PanelsSharedRefsContext';
import {useMaybeWorkspaceRefsContext} from '../../../../Workspace/WorkspaceRefsContext';
import {usePanelSettingsConfigUpdates} from '../../hooks/usePanelSettingsConfigUpdates';
import {
  makeCascadingPanelMediaConfigSelector,
  selectCustomRunColors,
} from '../mediaSettingsSelectors';
import {type ContextProviderState, SettingsLevel} from '../types';
import {MediaSettingsContextProvider} from './MediaSettingsContextProvider';
import {useRampFlagsForMediaSettings} from './utils';

export type MediaPanelSettingsProps = {
  panelRef: PanelTypes.Ref;
  panelBankSectionConfigRef: SectionRef;
};

/**
 * Provider for panel-level media settings.
 *
 * This provider wraps MediaSettingsContextProvider and internally uses
 * usePanelMediaSettingsDrawer to compute the context value.
 *
 * Usage:
 * ```tsx
 * <MediaPanelSettingsContextProvider panelRef={panelRef} panelBankSectionConfigRef={sectionRef}>
 *   <MediaSettingsDrawerContent ... />
 * </MediaPanelSettingsContextProvider>
 * ```
 */
export function MediaPanelSettingsContextProvider({
  children,
  panelRef,
  panelBankSectionConfigRef,
}: MediaPanelSettingsProps & {children: React.ReactNode}) {
  const contextValue = usePanelMediaSettingsDrawer({
    panelRef,
    panelBankSectionConfigRef,
  });
  return (
    <MediaSettingsContextProvider contextValue={contextValue}>
      {children}
    </MediaSettingsContextProvider>
  );
}

/**
 * Internal hook - do not use directly.
 * Use MediaPanelSettingsContextProvider instead and use useMediaSettingsContext, useMediaSettingsActions, or useMediaSettingsState to access the context value(s).
 */
const usePanelMediaSettingsDrawer = ({
  panelRef,
  panelBankSectionConfigRef,
}: MediaPanelSettingsProps): ContextProviderState => {
  const {config, viewType, updateConfig, unsetConfigFields} =
    usePanelSettingsConfigUpdates<MediaBrowserPanelConfig, 'Media Browser'>({
      panelRef,
      panelBankSectionConfigRef,
    });

  const mediaKeys =
    viewType === panelViews.mediaBrowser
      ? (config as MediaBrowserPanelConfig)?.mediaKeys
      : undefined;
  // This drawer is only supported for a single media panel which only allows a single media type.
  const sampledMediaCardTypes = useMediaCardTypes(mediaKeys);
  const sampledMediaCardType = sampledMediaCardTypes?.[0];
  // The data model allows a panel's keys to have different types, so record
  // which panels hit this to see whether taking the first type is enough.
  useEffect(() => {
    const distinctTypes = _.uniq(sampledMediaCardTypes ?? []);
    if (distinctTypes.length > 1) {
      recordRumActionOncePerView(
        'media_settings_drawer_multiple_media_types',
        {mediaCardTypes: distinctTypes, panelId: panelRef.id},
        `media_settings_drawer_multiple_media_types:${panelRef.id}`
      );
    }
  }, [sampledMediaCardTypes, panelRef.id]);
  const {customRunColorsRef, runSetRefs, customRunNamesRef} =
    usePanelsSharedRefsContext();

  const customRunColors = useMemoizedSelector(
    selectCustomRunColors,
    customRunColorsRef
  );
  const query = useRunSetsQuery({runSetRefs, customRunNamesRef});

  const panelConfig = config;
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const cascadedConfig = useMemoizedSelector(
    makeCascadingPanelMediaConfigSelector,
    workspaceRefs?.workspaceSettingsRef,
    panelBankSectionConfigRef,
    panelRef
  );

  // We need to load data about the current media shown in the panel so that we can know what to default
  // certain settings to.
  const panelQuery = useMemo(
    () => Spec.transformQuery(query, panelConfig),
    [query, panelConfig]
  );
  // loading these defaults shouldn't block the settings drawer
  // A panel with no keys yet has no media to load, or a type to wait on.
  const {data, loading} = useRunsData(panelQuery, !mediaKeys?.length);
  // Under NGW the sampled history keys can miss the panel's key, so fall back
  // to the type of the panel's own fetched media, as the panel itself does.
  const mediaCardType =
    sampledMediaCardType ??
    getMediaCardTypeFromHistories(data.histories.data, mediaKeys?.[0]);
  const {configWithDefaults, hasMasks, allBoxes, allMasks} =
    useConfigWithDefaults({
      data,
      query: panelQuery,
      config: cascadedConfig,
      customRunColors,
      pageQuery: query,
    });

  const updateConfigAndLog = useCallback(
    (configUpdate: Partial<MediaBrowserPanelConfig>) => {
      updateConfig(configUpdate);
      const updatedKeys = Object.keys(
        configUpdate
      ) as (keyof MediaBrowserPanelConfig)[];
      const preConfigUpdates = _.pick(configWithDefaults, updatedKeys);
      const eventData = {
        configUpdate: JSON.stringify(configUpdate),
        preConfigUpdate: JSON.stringify(preConfigUpdates),
        mediaType: mediaCardType,
        mode: configUpdate.mode ?? configWithDefaults.mode,
      };
      Analytics.track('Update Panel Media Settings', eventData);
    },
    [updateConfig, configWithDefaults, mediaCardType]
  );

  const unsetConfigFieldsAndLog = useCallback(
    (fields: Array<keyof MediaBrowserPanelConfig>) => {
      unsetConfigFields(fields);
      // `field` keeps the historical string shape for existing queries;
      // `fields` carries the structured list.
      Analytics.track('Unset Panel Media Setting', {
        field: fields.join(','),
        fields,
      });
    },
    [unsetConfigFields]
  );

  const unsetConfigFieldAndLog = useCallback(
    (field: keyof MediaBrowserPanelConfig) => unsetConfigFieldsAndLog([field]),
    [unsetConfigFieldsAndLog]
  );

  const boundingBoxes = useBoundingBoxes(
    allBoxes,
    panelConfig.mediaKeys,
    panelRef.id
  );
  const sliderRanges = useSliderRanges(panelConfig.mediaKeys, panelRef.id);
  const rampFlags = useRampFlagsForMediaSettings();

  return useMemo(
    () => ({
      config: configWithDefaults,
      configWithoutDefaults: panelConfig,
      hasMasks,
      hasBoundingBoxes: Object.keys(boundingBoxes).length > 0,
      loading,
      mediaCardTypes: mediaCardType ? [mediaCardType] : undefined,
      rampFlags,
      updateConfig: updateConfigAndLog,
      unsetConfigField: unsetConfigFieldAndLog,
      unsetConfigFields: unsetConfigFieldsAndLog,
      allBoxes: boundingBoxes,
      sliderRanges,
      allMasks,
      settingsLevel: SettingsLevel.PANEL,
      panelRef,
    }),
    [
      configWithDefaults,
      panelConfig,
      panelRef,
      hasMasks,
      boundingBoxes,
      loading,
      mediaCardType,
      rampFlags,
      updateConfigAndLog,
      unsetConfigFieldAndLog,
      unsetConfigFieldsAndLog,
      sliderRanges,
      allMasks,
    ]
  );
};
